import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import Button from '@/components-latest/primitives/Button'
import CircularButton from '@/components-latest/primitives/CircularButton'
import ContentBlock from '@/components-latest/primitives/ContentBlock'
import { Info } from '@/components-latest/primitives/Icons'
import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import BankInformationModal from '@/components-latest/ui/BillingPage/BankInformationModal'
import BillingRevenue from '@/components-latest/ui/BillingPage/BillingRevenue/BillingRevenue'
import CycleInfo from '@/components-latest/ui/BillingPage/CycleInfo'
import HowItWorksModal from '@/components-latest/ui/BillingPage/HowItWorksModal'
import AcceptanceRate from '@/components-latest/ui/PerformancePage/AcceptanceRate'
import OrdersDoughnut from '@/components-latest/ui/PerformancePage/OrdersDoughnut'
import {
  useFetchBankInformationQuery,
  useLazyFetchBillingCycleDetailsQuery
} from '@/ducks/billing/api/billing.api'
import { IBillingCycle } from '@/ducks/billing/billing.types'
import { OrdersPerformance } from '@/ducks/performance/performance.types'
import { selectStoreId } from '@/ducks/store/selectors/store.selectors'

import Styles from './BillingCycleDetails.module.scss'

type Props = {
  cycle: IBillingCycle | null
  type: 'paid' | 'unpaid' | null
}
const BillingCycleDetails = ({ cycle, type }: Props) => {
  const { t } = useTranslation()
  const storeId = useSelector(selectStoreId) || ''
  const [fetchCycleDetails, { isFetching, data }] =
    useLazyFetchBillingCycleDetailsQuery()

  const fetchBankQuery = useFetchBankInformationQuery({ storeId })

  useEffect(() => {
    if (!storeId || !cycle?.billing_cycle) return
    fetchCycleDetails({ storeId, cycleId: cycle.billing_cycle })
  }, [cycle, storeId, fetchCycleDetails])

  const mappedPerformanceData = useMemo(() => {
    if (!data?.OrderPerformance) return
    const performance = data?.OrderPerformance

    return Object.fromEntries(
      Object.entries(performance).map(([key, value]) => {
        if (typeof value === 'number') return [key, value]

        const restKeysMapper = {
          completed: 'delivered',
          canceled: 'cancelled',
          ongoing: 'inprogress'
        } as { [key: string]: keyof OrdersPerformance }

        return [
          key in restKeysMapper ? restKeysMapper[key] : key,
          { count: value.count, percentage: `${value.rate}` }
        ]
      })
    )
  }, [data]) as OrdersPerformance | undefined

  if (!cycle) return null

  return (
    <ContentBlock className={Styles.wrapper}>
      {isFetching ? (
        <div className={Styles.spinner}>
          <Spinner />
        </div>
      ) : (
        <>
          <CycleInfo cycle={cycle} type={type || 'unpaid'} />
          <div className={Styles.title}>
            <Text variant="nextTitle-3" weight="extraBold">
              {t('billing.details.revenue-title')}
            </Text>
            <CircularButton
              variant="flat"
              size="normal"
              className={Styles.infoButton}>
              <HowItWorksModal type={data?.revenue_type}>
                <Info />
              </HowItWorksModal>
            </CircularButton>
          </div>
          <BillingRevenue
            revenue={data?.revenue_details}
            revenueType={data?.revenue_type}
          />
          {mappedPerformanceData ? (
            <>
              <div className={Styles.title}>
                <Text variant="nextTitle-3" weight="extraBold">
                  {t('billing.details.orders-title')}
                </Text>
              </div>
              <div className={Styles.ordersPerformance}>
                <AcceptanceRate
                  received={mappedPerformanceData.received}
                  rejected={{
                    count: mappedPerformanceData.rejected.count,
                    percentage: mappedPerformanceData.rejected.percentage
                  }}
                  accepted={{
                    count: mappedPerformanceData.accepted.count,
                    percentage: mappedPerformanceData.accepted.percentage
                  }}
                  className={Styles.rate}
                />
                <div className={Styles.doughnutWrapper}>
                  <div className={Styles.doughnut}>
                    <OrdersDoughnut.Chart
                      ordersPerformance={mappedPerformanceData}
                    />
                  </div>
                  <OrdersDoughnut.Legend
                    ordersPerformance={mappedPerformanceData}
                  />
                </div>
              </div>
            </>
          ) : null}
          {data?.revenue_type?.toLowerCase() !== 'france' && (
            <BankInformationModal
              banks={fetchBankQuery.data?.banks}
              final_admin_earnings={
                data?.revenue_details?.final_admin_earnings || 0
              }>
              <Button
                variant="secondary"
                size="medium"
                className={Styles.banksButton}>
                Yassir bank information
              </Button>
            </BankInformationModal>
          )}
        </>
      )}
    </ContentBlock>
  )
}

export default BillingCycleDetails
